import React, { useState, useEffect } from 'react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Trash2, X, Edit2, Search, CheckSquare, Square,
  Maximize2, Power, PowerOff
} from 'lucide-react';

const TamanhosManager = () => {
  const [loading, setLoading] = useState(false);
  const [tamanhos, setTamanhos] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTamanho, setEditingTamanho] = useState(null);
  const [selectedTamanhos, setSelectedTamanhos] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [confirmInativar, setConfirmInativar] = useState({ show: false, id: null });
  const [confirmExcluir, setConfirmExcluir] = useState({ show: false, id: null });
  const [confirmInativarSelecionados, setConfirmInativarSelecionados] = useState({ show: false });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'TAMANHO',
    descricao: '',
    ordem: 0,
    ativo: true
  });

  // Tipos de tamanho disponíveis
  const tiposTamanho = [
    { value: 'TAMANHO', label: 'Tamanho (P, M, G)' },
    { value: 'MEDIDA', label: 'Medida (cm, mm)' },
    { value: 'CALCE', label: 'Calce (38, 40, 42)' },
    { value: 'OUTROS', label: 'Outros' }
  ];

  useEffect(() => {
    loadTamanhos();
  }, []);

  const loadTamanhos = async () => {
    setLoading(true);
    try {
      const params = {};
      if (showInactive) {
        params.ativo = 'all';
      }
      const response = await inventoryService.getTamanhos(params);
      if (response.success) {
        setTamanhos(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar tamanhos:', error);
      toast.error('Erro ao carregar tamanhos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTamanhos();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome do tamanho é obrigatório');
      return;
    }

    try {
      if (editingTamanho) {
        await inventoryService.updateTamanho(editingTamanho.id, formData);
        toast.success('Tamanho atualizado com sucesso!');
      } else {
        await inventoryService.createTamanho(formData);
        toast.success('Tamanho criado com sucesso!');
      }

      resetForm();
      loadTamanhos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar tamanho');
    }
  };

  const handleEdit = (tamanho) => {
    setEditingTamanho(tamanho);
    setFormData({
      nome: tamanho.nome || '',
      tipo: tamanho.tipo || 'TAMANHO',
      descricao: tamanho.descricao || '',
      ordem: tamanho.ordem || 0,
      ativo: tamanho.ativo
    });
    setShowForm(true);
  };

  // Inativar tamanho
  const handleInactivate = async (id) => {
    setConfirmInativar({ show: true, id });
  };

  const confirmInativarAction = async () => {
    const { id } = confirmInativar;
    try {
      await inventoryService.deleteTamanho(id);
      toast.success('Tamanho inativado com sucesso!');
      loadTamanhos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao inativar tamanho');
    }
    setConfirmInativar({ show: false, id: null });
  };

  // Ativar tamanho
  const handleActivate = async (id) => {
    try {
      await inventoryService.updateTamanho(id, { ativo: true });
      toast.success('Tamanho ativado com sucesso!');
      loadTamanhos();
    } catch (error) {
      toast.error('Erro ao ativar tamanho');
    }
  };

  // Excluir definitivamente
  const handleDelete = async (id) => {
    setConfirmExcluir({ show: true, id });
  };

  const confirmExcluirAction = async () => {
    const { id } = confirmExcluir;
    try {
      await inventoryService.deleteTamanho(id);
      toast.success('Tamanho excluído com sucesso!');
      loadTamanhos();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao excluir tamanho';
      toast.error(msg);
    }
    setConfirmExcluir({ show: false, id: null });
  };

  // Seleção individual
  const toggleSelectTamanho = (id) => {
    setSelectedTamanhos(prev =>
      prev.includes(id)
        ? prev.filter(t => t !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    if (selectedTamanhos.length === filteredTamanhos.length) {
      setSelectedTamanhos([]);
    } else {
      setSelectedTamanhos(filteredTamanhos.map(t => t.id));
    }
  };

  // Inativar selecionados
  const handleInactivateSelected = async () => {
    if (selectedTamanhos.length === 0) {
      toast.error('Nenhum tamanho selecionado');
      return;
    }

    setConfirmInativarSelecionados({ show: true });
  };

  const confirmInativarSelecionadosAction = async () => {
    try {
      setLoading(true);
      for (const id of selectedTamanhos) {
        await inventoryService.deleteTamanho(id);
      }
      toast.success(`${selectedTamanhos.length} tamanho(s) inativado(s) com sucesso!`);
      setSelectedTamanhos([]);
      loadTamanhos();
    } catch (error) {
      toast.error('Erro ao inativar tamanhos');
    } finally {
      setLoading(false);
    }
    setConfirmInativarSelecionados({ show: false });
  };

  // Filtrar tamanhos
  const filteredTamanhos = tamanhos.filter(tamanho => {
    const matchesSearch = !search ||
      tamanho.nome.toLowerCase().includes(search.toLowerCase()) ||
      (tamanho.descricao && tamanho.descricao.toLowerCase().includes(search.toLowerCase()));

    if (showInactive) {
      return matchesSearch;
    }
    return matchesSearch && tamanho.ativo;
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingTamanho(null);
    setFormData({
      nome: '',
      tipo: 'TAMANHO',
      descricao: '',
      ordem: 0,
      ativo: true
    });
  };

  // Obter label do tipo
  const getTipoLabel = (tipo) => {
    const found = tiposTamanho.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  // Obter cor do tipo
  const getTipoColor = (tipo) => {
    const colors = {
      'TAMANHO': 'bg-blue-100 text-blue-800',
      'MEDIDA': 'bg-green-100 text-green-800',
      'CALCE': 'bg-purple-100 text-purple-800',
      'OUTROS': 'bg-gray-100 text-gray-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tamanhos</h1>
          <p className="text-gray-600">Gerencie os tamanhos de produtos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Novo Tamanho
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Mostrar inativos</span>
        </label>

        {selectedTamanhos.length > 0 && (
          <button
            onClick={handleInactivateSelected}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <PowerOff className="w-4 h-4" />
            Inativar ({selectedTamanhos.length})
          </button>
        )}
      </div>

      {/* Tamanhos List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedTamanhos.length === filteredTamanhos.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedTamanhos.length === filteredTamanhos.length && filteredTamanhos.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ordem
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : filteredTamanhos.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Nenhum tamanho encontrado
                </td>
              </tr>
            ) : (
              filteredTamanhos.map((tamanho) => (
                <tr key={tamanho.id} className={`hover:bg-gray-50 ${selectedTamanhos.includes(tamanho.id) ? 'bg-blue-50' : ''} ${!tamanho.ativo ? 'bg-gray-100' : ''}`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleSelectTamanho(tamanho.id)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      {selectedTamanhos.includes(tamanho.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Maximize2 className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-sm font-medium text-gray-900">{tamanho.nome}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTipoColor(tamanho.tipo)}`}>
                      {getTipoLabel(tamanho.tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {tamanho.descricao || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {tamanho.ordem || 0}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tamanho.ativo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tamanho.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {tamanho.ativo ? (
                        <button
                          onClick={() => handleInactivate(tamanho.id)}
                          className="text-orange-600 hover:text-orange-800 p-1"
                          title="Inativar"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(tamanho.id)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Ativar"
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(tamanho)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tamanho.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir definitivamente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingTamanho ? 'Editar Tamanho' : 'Novo Tamanho'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Tamanho *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: P, M, G, 38, 40cm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Tamanho
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {tiposTamanho.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordem de Exibição
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.ordem}
                  onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Ordem em que será exibido nas listas</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Descrição adicional do tamanho..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Tamanho ativo
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingTamanho ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmInativar.show}
        onConfirm={confirmInativarAction}
        onCancel={() => setConfirmInativar({ show: false, id: null })}
        title="Inativar Tamanho"
        message="Tem certeza que deseja inativar este tamanho?"
        type="warning"
      />

      <ConfirmModal
        show={confirmExcluir.show}
        onConfirm={confirmExcluirAction}
        onCancel={() => setConfirmExcluir({ show: false, id: null })}
        title="Excluir Tamanho"
        message="ATENÇÃO: Exclusão definitiva! O tamanho será removido permanentemente. Continuar?"
        type="danger"
      />

      <ConfirmModal
        show={confirmInativarSelecionados.show}
        onConfirm={confirmInativarSelecionadosAction}
        onCancel={() => setConfirmInativarSelecionados({ show: false })}
        title="Inativar Tamanhos"
        message={`Tem certeza que deseja inativar ${selectedTamanhos.length} tamanho(s)?`}
        type="warning"
      />
    </div>
  );
};

export default TamanhosManager;
